import { ReactElement } from 'react';
import RawDataOverviewContainer from './OverviewContainer';
import BoxAndWhiskerChart, { AxisMode } from '../BoxAndWhiskerChart';
import { ParametersType } from '../../constants/telosair-interfaces';
import Deque from '../../classes/deque';
import useStorageState from '../../hooks/useStorageState';
import { Filters } from '../../services/localstorage.service';

type ComparisonProps = {
  onZoom?: () => void;
  onBackPress?: () => void;
  isZoomed?: boolean;
  sectionName: string;
  selectedParameters: ParametersType[];
  selectedDevices: { label: string; value: number }[];
  isDataLoading: boolean;
  chartGraphData: Record<string, Deque>;
  localParameterList: any[];
};
/**
 * React component for Comparison.
 * @param props - Properties of Comparison component.
 * @returns - React component for Comparison.
 */
export default function Comparison(props: ComparisonProps): ReactElement {
  const {
    onZoom,
    onBackPress,
    isZoomed,
    sectionName,
    chartGraphData,
    selectedParameters,
    isDataLoading,
    selectedDevices,
    localParameterList
  } = props;

  // Whether devices or parameters sit on the x-axis - retained across visits.
  const [axisMode, setAxisMode] = useStorageState<AxisMode>(Filters.boxPlotAxisMode, 'device');

  const axisModeToggle = (
    <div className="flex items-center gap-2">
      <span className="text-xs font-medium text-gray-400">X-Axis</span>
      <div className="flex rounded border border-gray-700 overflow-hidden">
        {(['device', 'parameter'] as AxisMode[]).map((mode) => (
          <button
            key={mode}
            type="button"
            onClick={() => setAxisMode(mode)}
            className={`px-3 py-1 text-xs font-medium transition-colors ${
              axisMode === mode
                ? 'bg-primary-500 text-white'
                : 'bg-container-500 text-gray-400 hover:text-gray-200'
            }`}
          >
            {mode === 'device' ? 'Devices' : 'Parameters'}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <RawDataOverviewContainer
      onBackPress={onBackPress}
      size={isZoomed ? 'full' : '1/2'}
      onZoom={onZoom}
      sectionName={sectionName}
      zoomed={isZoomed}
      loading={isDataLoading}
      headerActions={axisModeToggle}
      noDataText={
        (selectedParameters.length === 0 && 'Please select a parameter') ||
        (Object.keys(chartGraphData).length === 0 && 'Data unavailable')
      }
    >
      <BoxAndWhiskerChart
        isZoomed={isZoomed}
        isDataLoading={isDataLoading}
        selectedParameters={selectedParameters}
        chartGraphData={chartGraphData}
        localParameterList={localParameterList}
        selectedDevices={selectedDevices}
        axisMode={axisMode}
      />
    </RawDataOverviewContainer>
  );
}
